// FileManager: File I/O with File System Access API and fallback
import { FileOpenResult } from './types';

export class FileManager {
  private supportsFileSystemAccess: boolean;

  constructor() {
    this.supportsFileSystemAccess = 'showOpenFilePicker' in window;
  }

  async openFile(): Promise<FileOpenResult> {
    if (this.supportsFileSystemAccess) {
      return await this.openFileWithFSA();
    } else {
      return await this.openFileWithInput();
    }
  }

  private async openFileWithFSA(): Promise<FileOpenResult> {
    try {
      const [fileHandle] = await (window as any).showOpenFilePicker({
        types: [
          {
            description: 'Text Files',
            accept: {
              'text/*': ['.txt', '.js', '.ts', '.py', '.html', '.css', '.json', '.md'],
            },
          },
        ],
      });

      const file = await fileHandle.getFile();
      const content = await file.text();

      return {
        name: file.name,
        content,
        handle: fileHandle,
      };
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('File selection cancelled');
      }
      throw error;
    }
  }

  private async openFileWithInput(): Promise<FileOpenResult> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '*/*';

      input.onchange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];
        if (!file) {
          reject(new Error('No file selected'));
          return;
        }

        const content = await file.text();
        resolve({
          name: file.name,
          content,
        });
      };

      input.oncancel = () => {
        reject(new Error('File selection cancelled'));
      };

      input.click();
    });
  }

  async saveFile(content: string, handle?: FileSystemFileHandle): Promise<FileSystemFileHandle | null> {
    if (this.supportsFileSystemAccess && handle) {
      try {
        const writable = await (handle as any).createWritable();
        await writable.write(content);
        await writable.close();
        return handle;
      } catch (error) {
        console.error('Failed to save file with FSA:', error);
        // Fallback to download
        return null;
      }
    }
    return null;
  }

  async saveAsFile(content: string, suggestedName: string): Promise<FileSystemFileHandle | null> {
    if (this.supportsFileSystemAccess) {
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName,
          types: [
            {
              description: 'Text Files',
              accept: {
                'text/*': ['.txt', '.js', '.ts', '.py', '.html', '.css', '.json', '.md'],
              },
            },
          ],
        });

        const writable = await handle.createWritable();
        await writable.write(content);
        await writable.close();
        return handle;
      } catch (error: any) {
        if (error.name === 'AbortError') {
          return null; // User cancelled
        }
        console.error('Failed to save file with FSA:', error);
      }
    }

    // Fallback: download
    this.downloadFile(content, suggestedName);
    return null;
  }

  downloadFile(content: string, fileName: string): void {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async handleFileDrop(event: DragEvent): Promise<FileOpenResult> {
    event.preventDefault();
    const items = event.dataTransfer?.items;
    if (!items || items.length === 0) {
      throw new Error('No file dropped');
    }

    const item = items[0];
    if (item.kind !== 'file') {
      throw new Error('Dropped item is not a file');
    }

    const file = item.getAsFile();
    if (!file) {
      throw new Error('Could not get file from dropped item');
    }

    const content = await file.text();

    // Try to get file handle if FSA is supported
    let handle: FileSystemFileHandle | undefined;
    if (this.supportsFileSystemAccess && 'getAsFileSystemHandle' in item) {
      try {
        handle = (await (item as any).getAsFileSystemHandle()) as FileSystemFileHandle;
      } catch {
        // FSA handle not available
      }
    }

    return {
      name: file.name,
      content,
      handle,
    };
  }
}
